/**
 * Serviço de Minhas Obras — CRUD no Google Drive
 *
 * Arquivo: Bigwa_App/MinhasObras/minhas-obras.json
 * Separado do referencias.json — ciclo de vida independente.
 */

import { googleDrive } from '../googleDrive';
import { getTokenOrRefresh } from '../googleAuth';
import { DRIVE_API_BASE } from '../googleDrive/core';
import { driveFetch } from '../googleDrive/driveFetch';
import type { MinhasObrasData, MinhaObra, MinhaObraWorkType } from './types';
import { logger } from '../../utils/logger';

const FILE_NAME = 'minhas-obras.json';
const FOLDER_NAME = 'MinhasObras';

function emptyData(): MinhasObrasData {
  return {
    version: '1.0',
    updatedAt: new Date().toISOString(),
    obras: [],
  };
}

// In-memory cache to avoid repeated Drive reads within a session
let cache: MinhasObrasData | null = null;

// Serializa operações de escrita para evitar race conditions entre abas/chamadas concorrentes
let writeQueue: Promise<void> = Promise.resolve();

/**
 * Garante a pasta MinhasObras e retorna o ID do arquivo minhas-obras.json (ou null)
 */
async function findFile(): Promise<{ folderId: string; fileId: string | null }> {
  const folderId = await googleDrive.findOrCreateSubfolder(FOLDER_NAME);

  const accessToken = await getTokenOrRefresh();

  const query = `name='${FILE_NAME}' and '${folderId}' in parents and trashed=false`;
  const url = `${DRIVE_API_BASE}/files?q=${encodeURIComponent(query)}&fields=files(id)`;

  const res = await driveFetch(url, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });

  if (!res.ok) throw new Error(`Search failed: ${res.statusText}`);
  const data = await res.json();

  return {
    folderId,
    fileId: data.files?.[0]?.id ?? null,
  };
}

export async function loadMinhasObras(): Promise<MinhasObrasData> {
  if (cache) return cache;

  try {
    const { fileId } = await findFile();
    if (!fileId) {
      cache = emptyData();
      return cache;
    }

    const accessToken = await getTokenOrRefresh();

    const res = await driveFetch(`${DRIVE_API_BASE}/files/${fileId}?alt=media`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (!res.ok) throw new Error(`Load failed: ${res.statusText}`);

    const json = await res.json() as MinhasObrasData;
    cache = json;
    return json;
  } catch (error) {
    logger.error('MinhasObrasService', 'loadMinhasObras failed:', error);
    // Não cacheia dados vazios em caso de erro de rede — próxima chamada tentará novamente
    return emptyData();
  }
}

export async function saveMinhasObras(data: MinhasObrasData): Promise<void> {
  data.updatedAt = new Date().toISOString();
  cache = data;

  const accessToken = await getTokenOrRefresh();

  const { folderId, fileId } = await findFile();
  const content = JSON.stringify(data, null, 2);

  if (fileId) {
    // Update existing
    const url = `https://www.googleapis.com/upload/drive/v3/files/${fileId}?uploadType=media`;
    const res = await driveFetch(url, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: content,
    });
    if (!res.ok) throw new Error(`Update failed: ${res.statusText}`);
  } else {
    // Create new
    const metadata = {
      name: FILE_NAME,
      mimeType: 'application/json',
      parents: [folderId],
    };
    const form = new FormData();
    form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
    form.append('file', new Blob([content], { type: 'application/json' }));

    const url = 'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart';
    const res = await driveFetch(url, {
      method: 'POST',
      headers: { Authorization: `Bearer ${accessToken}` },
      body: form,
    });
    if (!res.ok) throw new Error(`Create failed: ${res.statusText}`);
  }
}

/**
 * Serializa uma operação de escrita para evitar race conditions.
 * Todas as mutações passam por aqui.
 */
function enqueueWrite(fn: () => Promise<void>): Promise<void> {
  writeQueue = writeQueue.then(fn, fn);
  return writeQueue;
}

export function confirmObra(id: string): Promise<void> {
  return enqueueWrite(async () => {
    const data = await loadMinhasObras();
    const obra = data.obras.find(o => o.id === id);
    if (!obra) {
      logger.warn('MinhasObrasService', `confirmObra: obra ${id} não encontrada`);
      return;
    }

    obra.status = 'confirmed';
    obra.confirmedAt = new Date().toISOString();
    await saveMinhasObras(data);
  });
}

export function rejectObra(id: string): Promise<void> {
  return enqueueWrite(async () => {
    const data = await loadMinhasObras();
    const obra = data.obras.find(o => o.id === id);
    if (!obra) {
      logger.warn('MinhasObrasService', `rejectObra: obra ${id} não encontrada`);
      return;
    }

    obra.status = 'rejected';
    await saveMinhasObras(data);
  });
}

export function addObraPendente(obra: MinhaObra): Promise<void> {
  return enqueueWrite(async () => {
    const data = await loadMinhasObras();
    // Não duplicar
    if (data.obras.some(o => o.workId === obra.workId)) return;

    data.obras.push(obra);
    await saveMinhasObras(data);
  });
}

export async function addObraManual(
  obra: Omit<MinhaObra, 'id' | 'detectedAt' | 'status' | 'detectionSource'>
): Promise<void> {
  const data = await loadMinhasObras();

  const newObra: MinhaObra = {
    ...obra,
    id: crypto.randomUUID(),
    detectedAt: new Date().toISOString(),
    status: 'confirmed',
    confirmedAt: new Date().toISOString(),
    detectionSource: 'manual',
  };

  data.obras.push(newObra);
  await saveMinhasObras(data);
}

export async function updateObra(id: string, fields: Partial<MinhaObra>): Promise<void> {
  const data = await loadMinhasObras();
  const obra = data.obras.find(o => o.id === id);
  if (!obra) return;

  Object.assign(obra, fields);
  await saveMinhasObras(data);
}

export function getObrasPendentes(data: MinhasObrasData): MinhaObra[] {
  return data.obras.filter(o => o.status === 'pending');
}

export function getObrasConfirmadas(data: MinhasObrasData): MinhaObra[] {
  return data.obras.filter(o => o.status === 'confirmed');
}

export function getObrasByType(data: MinhasObrasData, type: MinhaObraWorkType): MinhaObra[] {
  return data.obras.filter(o => o.type === type && o.status === 'confirmed');
}

/**
 * Invalida o cache (usar quando dados podem ter mudado externamente)
 */
export function invalidateCache(): void {
  cache = null;
}
